import AutocompleteSearchInput from '@/components/form/AutocompleteSearchInput'
import SearchInput from '@/components/form/SearchInput'
import {
  FloristStatusBadge,
  getFloristStatusLabel,
} from '@/components/organizations/FloristStatusBadge'
import { ActionRow } from '@/components/patterns/action-row'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  FloristMap,
  hasPin,
  showWholeCountry,
  type PinnedFloristLocation,
} from '@/components/reports/FloristMap'
import { Button } from '@/components/ui/button'
import { AddressFormatter } from '@/components/ui/address-formatter'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { usePlacesAutocomplete, type PlaceSuggestion } from '@/hooks/use-places-autocomplete'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { isGlobalOrganization } from '@/lib/user-access'
import {
  floristStatusSchema,
  type FloristStatusFilter,
} from '@/lib/services/organizations/organization.schema'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { reportQueries } from '@/lib/services/reports/reports.query'
import {
  DEFAULT_FLORIST_MAP_STATUSES,
  floristMapSearchSchema,
} from '@/lib/services/reports/reports.schema'
import type { FloristMapLocation } from '@/lib/services/reports/reports.types'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { ChevronDown, MapPinned } from 'lucide-react'
import { useDebounce } from '@uidotdev/usehooks'
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react'

export const Route = createFileRoute('/_authenticated/$orgId/reports/florist-map/')({
  component: FloristMapReportPage,
  head: () => adminTitleHead('Florist Map', 'Reports'),
  validateSearch: (search) => floristMapSearchSchema.parse(search),
  loader: async ({ context, params }) => {
    if (!context.ability.can('access', 'global')) throw notFound()

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))

    if (!isGlobalOrganization(organization)) throw notFound()

    void context.queryClient.prefetchQuery(
      reportQueries.floristMap({ organizationId: params.orgId })
    )

    return { isReadOnly: true }
  },
})

function byShopName(a: FloristMapLocation, b: FloristMapLocation): number {
  return a.organization_name.localeCompare(b.organization_name)
}

/** Text as the shop search compares it: lowercase letters and digits, without accents. */
function searchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/** What a typed search looks for: a pasted link's domain, without "www.", or the text itself. */
function searchTerm(query: string): string {
  const trimmed = query.trim()
  const text =
    /^https?:\/\//i.test(trimmed) && URL.canParse(trimmed) ? new URL(trimmed).hostname : trimmed

  return searchText(text.replace(/^www\./i, ''))
}

/** Whether a shop's name or domain contains the search (already passed through `searchTerm`). */
function matchesShopSearch(location: FloristMapLocation, term: string): boolean {
  return [location.organization_name, location.primary_domain ?? ''].some((text) =>
    searchText(text).includes(term)
  )
}

/** The pinned shops the status filter and a search (passed through `searchTerm`) show. */
function pinnedMatches(
  locations: readonly FloristMapLocation[],
  statuses: readonly string[],
  term: string
): PinnedFloristLocation[] {
  return locations
    .filter(hasPin)
    .filter((location) => statuses.includes(location.florist_status))
    .filter((location) => !term || matchesShopSearch(location, term))
}

function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

const statusList = new Intl.ListFormat('en', { type: 'conjunction' })

export function FloristMapReportPage() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: locations } = useSuspenseQuery(reportQueries.floristMap({ organizationId: orgId }))
  const { fetchSuggestions, getPlaceViewport } = usePlacesAutocomplete()
  const mapRef = useRef<google.maps.Map | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const placeInputRef = useRef<HTMLInputElement>(null)
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [place, setPlace] = useState('')
  const [shopSearch, setShopSearch] = useState(search.search ?? '')
  // The map moves to the matches once typing pauses, not on every keystroke.
  const settledShopSearch = useDebounce(shopSearch, 500)
  // Google's suggestions, with the text they answer.
  const [placeSuggestions, setPlaceSuggestions] = useState<{
    query: string
    suggestions: PlaceSuggestion[]
  }>({ query: '', suggestions: [] })
  const debouncedPlace = useDebounce(place, 300)

  const statuses: readonly string[] = search.status ?? DEFAULT_FLORIST_MAP_STATUSES
  // The list and pins follow the search a beat behind the box, so typing never waits on them.
  const term = searchTerm(useDeferredValue(shopSearch))
  const matches = term
    ? locations.filter((location) => matchesShopSearch(location, term))
    : locations
  const shown = matches.filter((location) => statuses.includes(location.florist_status))
  // Memoized so the map redraws its pins only when the data, status filter or search changes, not
  // on every pan. The router keeps `search.status` the same array until the URL changes.
  const pinned = useMemo(
    () => pinnedMatches(locations, statuses, term),
    [locations, statuses, term]
  )
  const settledTerm = searchTerm(settledShopSearch)
  const frame = settledTerm ? pinnedMatches(locations, statuses, settledTerm) : null
  // A search lists every match, wherever the map is; otherwise the list follows the map.
  const listed = (
    term
      ? pinned
      : pinned.filter(
          (location) => !bounds || bounds.contains({ lat: location.lat, lng: location.lng })
        )
  ).toSorted(byShopName)
  // Matches the status filter hides, so the search can offer them instead of finding nothing.
  const hiddenMatches = term
    ? matches.filter((location) => !statuses.includes(location.florist_status))
    : []
  const hiddenStatuses = floristStatusSchema.options.filter((status) =>
    hiddenMatches.some((location) => location.florist_status === status)
  )
  const unpinned = shown.filter((location) => !hasPin(location)).toSorted(byShopName)
  // A shop the status filter hides is no longer selected.
  const selected = shown.find((location) => location.id === selectedId) ?? null
  const floristCount = new Set(shown.map((location) => location.organization_id)).size
  const stateCount = new Set(shown.flatMap((location) => location.state ?? [])).size

  // Google's place suggestions are an outside system; ask again once the typed place settles.
  useEffect(() => {
    if (debouncedPlace.trim().length < 3) return

    let isCurrent = true
    void fetchSuggestions(debouncedPlace, { includedPrimaryTypes: ['(regions)'] }).then(
      (suggestions) => {
        if (isCurrent) setPlaceSuggestions({ query: debouncedPlace, suggestions })
      }
    )

    return () => {
      isCurrent = false
    }
  }, [debouncedPlace, fetchSuggestions])

  // Keep the settled search in the URL, so a link opens on it. Not on every keystroke: each URL
  // change re-renders every link in the app, which made typing lag.
  const writeSearchToUrl = useEffectEvent((value: string | undefined) => {
    if (value !== search.search) {
      void navigate({ search: (previous) => ({ ...previous, search: value }), replace: true })
    }
  })
  useEffect(() => writeSearchToUrl(settledShopSearch.trim() || undefined), [settledShopSearch])

  const showStatuses = (isShown: (status: FloristStatusFilter) => boolean) => {
    void navigate({
      search: (previous) => ({ ...previous, status: floristStatusSchema.options.filter(isShown) }),
      replace: true,
    })
  }

  const setStatus = (status: FloristStatusFilter, isShown: boolean) =>
    showStatuses((option) => (option === status ? isShown : statuses.includes(option)))

  const showPlace = async (suggestion: PlaceSuggestion) => {
    setPlace(suggestion.name)
    // Searching is done; leaving the box keeps the suggestions for the new text from reopening.
    placeInputRef.current?.blur()
    const viewport = await getPlaceViewport(suggestion.value)
    if (viewport) mapRef.current?.fitBounds(viewport, 0)
  }

  // Back to the opening view: the whole country, nothing picked, nothing searched.
  const resetZoom = () => {
    setSelectedId(null)
    setPlace('')
    setShopSearch('')
    if (mapRef.current) showWholeCountry(mapRef.current)
  }

  const selectFromMap = (location: PinnedFloristLocation) => {
    setSelectedId(location.id)
    const list = listRef.current
    const row = list?.querySelector(`[data-location-id="${location.id}"]`)
    if (!list || !row) return

    // Scroll only the list, centering the row: scrollIntoView would also scroll the page, which
    // moves the map out of sight when the list sits below it on narrow screens.
    const rowTop = row.getBoundingClientRect().top - list.getBoundingClientRect().top
    list.scrollTo({ top: list.scrollTop + rowTop - (list.clientHeight - row.clientHeight) / 2 })
  }

  // Stable, so the list's rows skip re-rendering while a search changes which rows show.
  const selectFromList = useCallback((location: FloristMapLocation) => {
    setSelectedId(location.id)
    if (!hasPin(location)) return

    // Bring the map to the shop; the pop-up opens over it.
    mapRef.current?.panTo({ lat: location.lat, lng: location.lng })
    mapRef.current?.setZoom(Math.max(mapRef.current.getZoom() ?? 0, 14))
  }, [])

  return (
    <div className="@container/florist-map w-full space-y-4 pb-6">
      <AdminPageHeader
        icon={MapPinned}
        title="Florist Map"
        description="Where OneStem florists are. Search a ZIP code, city or state to jump there."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <AutocompleteSearchInput
            inputRef={placeInputRef}
            aria-label="Jump to a ZIP code, city or state"
            placeholder="Jump to a ZIP code, city or state"
            value={place}
            setValue={setPlace}
            // Only suggestions for exactly what's typed: an answer for earlier text (or one still
            // arriving after more typing) stays hidden, so Enter can't pick an old place.
            suggestions={placeSuggestions.query === place ? placeSuggestions.suggestions : []}
            onSelect={(suggestion) => void showPlace(suggestion)}
            getOptionKey={(suggestion) => suggestion.id}
            getOptionLabel={(suggestion) => suggestion.name}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              Status ({statuses.length})
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {floristStatusSchema.options.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={statuses.includes(status)}
                onCheckedChange={(checked) => setStatus(status, checked)}
                onSelect={(event) => event.preventDefault()}
              >
                <FloristStatusBadge status={status} />
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Typography variant="supporting" tone="muted">
          {counted(floristCount, 'florist')} · {counted(shown.length, 'location')} ·{' '}
          {counted(stateCount, 'state')}
        </Typography>
        <Button type="button" variant="link" size="inline" className="ml-auto" onClick={resetZoom}>
          Reset zoom
        </Button>
      </div>

      <div className="grid gap-4 @4xl/florist-map:grid-cols-[minmax(0,1fr)_22rem]">
        <Card size="flush" className="overflow-hidden">
          <FloristMap
            locations={pinned}
            frame={frame}
            mapRef={mapRef}
            selectedId={selectedId}
            popup={
              selected && hasPin(selected) ? (
                <FloristLocationDetails orgId={orgId} location={selected} />
              ) : null
            }
            onBoundsChange={setBounds}
            onSelect={selectFromMap}
            onDeselect={() => setSelectedId(null)}
            className="h-[70vh] min-h-96"
          />
        </Card>

        <Card size="flush" className="flex h-[70vh] min-h-96 flex-col overflow-hidden">
          {/* Stays above the list as it scrolls. */}
          <div className="border-b p-2">
            <SearchInput
              aria-label="Search florists by name or URL"
              placeholder="Search florists by name or URL"
              value={shopSearch}
              setValue={setShopSearch}
            />
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-2">
            {term && matches.length === 0 ? (
              <Typography variant="supporting" tone="muted" className="px-2 pt-2">
                No florists match that name or URL.
              </Typography>
            ) : null}
            {hiddenMatches.length > 0 ? (
              <Typography variant="supporting" tone="muted" className="px-2 pt-2">
                {hiddenMatches.length} more {hiddenMatches.length === 1 ? 'match' : 'matches'} in{' '}
                {statusList.format(hiddenStatuses.map(getFloristStatusLabel))}.{' '}
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  onClick={() =>
                    showStatuses(
                      (option) => statuses.includes(option) || hiddenStatuses.includes(option)
                    )
                  }
                >
                  Show {hiddenMatches.length === 1 ? 'it' : 'them'}
                </Button>
              </Typography>
            ) : null}
            {/* A search that lists nothing says why above instead. */}
            {term && listed.length === 0 ? null : (
              <Typography variant="eyebrow" tone="muted" className="px-2 py-1">
                {listed.length} {term ? 'on the map' : 'in view'}
              </Typography>
            )}
            {listed.map((location) => (
              <ShopRow
                key={location.id}
                location={location}
                selected={location.id === selectedId}
                onSelect={selectFromList}
              />
            ))}
            {unpinned.length > 0 ? (
              <>
                <Typography variant="eyebrow" tone="muted" className="mt-3 px-2 py-1">
                  No map pin ({unpinned.length})
                </Typography>
                {unpinned.map((location) => (
                  <div key={location.id}>
                    <ShopRow
                      location={location}
                      selected={location.id === selectedId}
                      onSelect={selectFromList}
                    />
                    {/* Without a pin there's nowhere on the map for the pop-up, so it opens here. */}
                    {location.id === selectedId ? (
                      <FloristLocationDetails orgId={orgId} location={location} />
                    ) : null}
                  </div>
                ))}
              </>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  )
}

// Memoized: a search adds and removes hundreds of rows per keystroke, and the rows that stay
// shouldn't render again.
const ShopRow = memo(function ShopRow({
  location,
  selected,
  onSelect,
}: {
  location: FloristMapLocation
  selected: boolean
  onSelect: (location: FloristMapLocation) => void
}) {
  return (
    <ActionRow
      padding="compact"
      selected={selected}
      data-location-id={location.id}
      onClick={() => onSelect(location)}
    >
      <span className="min-w-0">
        <Typography as="span" variant="label" className="block truncate">
          {location.organization_name}
        </Typography>
        <Typography as="span" variant="supporting" tone="muted" className="block">
          <AddressFormatter variant="cityState" address={location} />
        </Typography>
      </span>
    </ActionRow>
  )
})

/** A shop's details: in the map's pop-up, or in the list when the shop has no map pin. */
function FloristLocationDetails({
  orgId,
  location,
}: {
  orgId: number
  location: FloristMapLocation
}) {
  const path = usePathHelper()

  return (
    <div className="w-72 max-w-full space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <Typography variant="labelStrong">{location.organization_name}</Typography>
        <FloristStatusBadge status={location.florist_status} />
      </div>
      <Typography as="div" variant="supporting">
        <AddressFormatter variant="noCountry" address={location} />
      </Typography>
      {location.phone ? (
        <Typography as="p" variant="supporting">
          {formatPhone(location.phone)}
        </Typography>
      ) : null}
      {location.primary_domain ? (
        <Button asChild variant="link" size="inline" className="max-w-full">
          <a href={`https://${location.primary_domain}`} target="_blank" rel="noreferrer">
            {/* A long domain wraps instead of widening the pop-up into a scrollbar. */}
            <span className="min-w-0 break-all whitespace-normal">{location.primary_domain}</span>
          </a>
        </Button>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button asChild variant="outline" size="sm">
          <Link {...path.organizations.show(location.organization_id).using({ orgId }).link}>
            View florist
          </Link>
        </Button>
        {hasPin(location) ? null : (
          <Button asChild variant="outline" size="sm">
            <Link
              {...path.locations.show(location.id).using({ orgId: location.organization_id }).link}
            >
              Add a map pin
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}
